import throttle from 'lodash/throttle'

import socket from '@/libs/socket'

import auth from '@/mixins/auth'
import componentState from '@/mixins/component-state'
import scrollLock from '@/mixins/scroll-lock'
import editState from '@/mixins/edit-state'

import state from './state'
import unsavedContent from '@/mixins/unsaved-content'
import unsavedAttachments from '@/components/Forms/Create/mixins/unsaved-attachments'
import ysc from '@/mixins/ysc'
import gaTrackingMixin from '@/mixins/ga-tracking'
import contentViewLogMixin from '@/mixins/content-view-log'
import projectClicks from '@/api/project-clicks'
import displayTypes from '@/mixins/project-display-types'

export default {
  metaInfo: function () {
    return {
      title: `${this.title}`,
      meta: [
        { property: 'og:title', content: this.project.title },
        { property: 'og:type', content: this.project.type },
        { property: 'og:image', content: this.project.image },
        { property: 'og:description', content: this.project.description },
        { property: 'og:url', content: process.env.WEB_ROOT + '/project/' + this.project.slug },
        { property: 'twitter:url', content: process.env.WEB_ROOT + '/project/' + this.project.slug },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: this.project.title },
        { name: 'twitter:description', content: this.project.description },
        { name: 'twitter:image', content: this.project.image }
      ]
    }
  },

  mixins: [auth, componentState, scrollLock, editState, unsavedContent, unsavedAttachments, ysc, gaTrackingMixin, contentViewLogMixin, displayTypes],

  data: function () {
    return {
      nextPage: null,
      fromPostCreate: false,
      restoreScroll: 0,
      startTab: 'list',
      sortNewestFirst: false,
      collapseComments: false
    }
  },

  computed: {
    idOrSlug: function () {
      return this.$route.params.id
    },
    project: function () {
      return this._get(state.project.id) ? this.$store.getters.project(this._get(state.project.id)) || {} : {}
    },
    projectIsOnline () {
      return this._get(state.project.isOnline)
    },
    activeTab: function () {
      return this._get(state.activeTab)
    },
    projectLoading: function () {
      return this._get(state.project.loading)
    },
    notFound: function () {
      return this._get(state.project.notFound)
    },
    loaded: function () {
      return this._get(state.project.id) > 0
    },
    modalVisible: function () {
      return this.gridModalVisible || this.attachModalVisible
    },
    boardModalVisible: function () {
      return this.$store.getters.modalIsVisible('whiteboard')
    },
    gridModalVisible: function () {
      return this._get(state.grid.modalVisible, false)
    },
    attachModalVisible: function () {
      return this.$store.getters.modalIsVisible('attach')
    },
    title: function () {
      return this.project && this.project.id ? this.$isStories ? this.project.title : `${this.project.title}` : ''
    },
    fromTemplate: function () {
      return !!this.$route.params.fromTemplate
    },
    fromStartButtonTemplate: function () {
      return !!this.$route.params.startButton || this.fromTemplate
    },
    projectRole: function () {
      return this._get(state.project.role, 'none')
    },
    limit: function () {
      return this.activeTab === 'grid' ? 13 : 10
    },
    targetPostID: function () {
      return this.$route.query.postID
    },
    isSortedNewestFirst: function () {
      return !this._get(state.project.defaultFirstToLast, false) && this._get(state.posts.sortNewest, false)
    },
    repliesExpanded: function () {
      return this._get(state.posts.repliesExpanded, false)
    },
    projectPostsLoaded: function () {
      const posts = this.project.id > 0 ? this.$store.getters.projectPosts(this.project.id, this.isSortedNewestFirst) : []
      return posts.length > 0
    },
    hasUnsaved: function () {
      return !this.isDeletingInProgress && (this.hasUnsavedContent || this.hasUnsavedAttachments || (this.fromTemplate && !this.hasSavedOnce && this.project.displayType !== this.DisplayTypeRegistrationForm))
    },
    projectDisplayType: function () {
      return this._get(state.project.displayType)
    },
    isSnapshot: function () {
      return this.project.type === 'snapshot'
    },
    isTeamMember: function () {
      return this.projectRole && this.projectRole !== 'none'
    }
  },

  watch: {
    idOrSlug: function (id) {
      // if the id changed to a slug, we need to set the object key to the slug
      this._setObjectKey(this.keyName)
      this._set(state.project.id, 0)
      this.fetchProject()
    },
    modalVisible: function (visible) {
      if (visible) {
        this.restoreScroll = document.body.scrollTop || document.documentElement.scrollTop
      } else if (this.restoreScroll > 0) {
        const top = this.restoreScroll
        this.restoreScroll = 0
        window.scrollTo(1, top)
      }
    },
    '$route.query.postID': function () {
      this._set(state.activeTab, 'grid')
    },
    isLoggedIn: function () {
      this.fetchProject()
    }
  },

  beforeRouteEnter: function (to, from, next) {
    if (from.name === 'createPost') {
      next(vm => {
        vm.fromPostCreate = true
      })
    } else {
      next()
    }
  },

  beforeRouteUpdate: function (to, from, next) {
    const sameProject = (from.params.id === to.params.id || (this.project && this.project.slug === to.params.id))
    if (this.hasUnsaved && !sameProject) {
      this.$refs.unsavedModal.show()
      this.nextPage = next
    } else {
      next()
    }
  },

  beforeRouteLeave: function (to, from, next) {
    if (this.hasUnsaved) {
      this.$refs.unsavedModal.show()
      this.nextPage = next
    } else {
      if (!this.isDeletingInProgress && !this.$isEngCom && !this.showTabs) {
        // showTabs is false when a project is from a template and has not been saved
        if (this.$refs.projectHeader !== undefined) this.$refs.projectHeader.deleteProject(false)
      }
      next()
    }
  },

  created: function () {
    window.addEventListener('beforeunload', this.handleUnload)

    if (this.targetPostID) {
      if (!this.$isEngCom) {
        this.startTab = 'grid'
      }
    }

    if (this.isLoggedIn) {
      this.$store.dispatch('cacheLikedProjects', this.currentUser.userName)
      this.$store.dispatch('cacheLikedPosts', this.currentUser.userName)
      this.$store.dispatch('cacheLikedComments', this.currentUser.userName)
    }

    this.fetchProject()
  },

  destroyed: function () {
    this._clear()
    window.removeEventListener('beforeunload', this.handleUnload)
  },

  methods: {
    handleUnload: function (event) {
      if (this.hasUnsaved) {
        event.preventDefault()
        event.returnValue = ''
      }
    },
    initCallbacks: function () {
      this._register(state.posts.fetch, this.fetchPosts)
      this._register(state.posts.sort, this.sortPosts)
      this._register(state.posts.toggleReplies, this.toggleReplies)
      this._register('lockScrolling', this.lockScrolling)
      this._register('unlockScrolling', this.unlockScrolling)
      this._register('logLinkClick', this.logLinkClick)
      this.initLinkClicks()
    },
    getUserSettings: function () {
      return new Promise((resolve) => {
        if (this.isLoggedIn && this.$isProjects) {
          this.$store.dispatch('getUserSettings', this.currentUser.userName).then((settings) => {
            this.sortNewestFirst = !!settings.sortNewestFirst
            this.collapseComments = !!settings.collapseComments
            resolve()
          }, () => {
            this.sortNewestFirst = false
            this.collapseComments = false
            resolve()
          })
        } else {
          if (this.$isProjects) {
            this.sortNewestFirst = false
            this.collapseComments = false
          } else if (this.$isEngCom) {
            this.sortNewestFirst = false
            this.collapseComments = true
          }
          resolve()
        }
      })
    },
    fetchProject: function () {
      if (this.projectPostsLoaded && this.idOrSlug && (this.project.id === parseInt(this.idOrSlug) || this.project.slug === this.idOrSlug) && !(this.project.isGated || this.project.isLocked)) {
        return
      }
      this._clear()

      if (this.fromTemplate) {
        this._set(state.project.defaultFirstToLast, true)
        this._set(state.posts.hasModifiedPosts, false)
      } else {
        this._set(state.posts.hasModifiedPosts, true)
      }

      this._set(state.project.loading, true)
      this._set(state.project.notFound, false)
      this._set(state.project.id, 0)

      this._set(state.activeTab, null)
      this.getUserSettings().then(() => {
        this.$store.dispatch('getProject', this.$route.params).then((project) => {
          this.logContentView(project.id, this.$route.name, this.$route.query.utm_source ? this.$route.query.utm_source : '')
          // if the primary category of the project is 60 (STEM Game), redirect it to the external_url (url)
          if (project.categories?.length > 0 && project.categories[0].id === 60) {
            window.location.href = project.url
            return
          }

          let updateRoute = false
          if (project.id === parseInt(this.idOrSlug) && project.slug) {
            updateRoute = true
          } else if (project.type === 'story' && this.$route.name !== 'viewStory') {
            updateRoute = true
          } else if (project.type !== 'story' && project.type !== 'resource' && this.$route.name === 'viewStory') {
            updateRoute = true
          }
          if (updateRoute && !this.project.isGated) {
            const basePath = project.type === 'story' || project.type === 'resource' ? 'story' : 'project'
            this.$router.replace({ path: `/${basePath}/${project.slug}`, query: this.$route.query })
            return
          } else if (!this.$isStories && (project.type === 'story' || project.type === 'resource')) {
            window.open(`https://engineering.com/story/${this.$route.params.id}`, '_self')
          }
          // Get resource type here
          this.initCallbacks()
          if (!this.fromStartButtonTemplate) {
            this._set(state.activeTab, 'list')
          }
          this._set(state.project.loading, false)
          this._set(state.project.id, project.id)
          this._set(state.project.isStory, project.type === 'story')
          this._set(state.project.isOnline, project.isOnline)
          this._set(state.project.displayType, project.displayType)
          if (this.isLoggedIn) {
            this.$store.dispatch('getMembership', { project, user: this.currentUser }).then((membership) => {
              this._set(state.project.role, membership ? membership.role : 'none')
            })
          }
          // Check if the project has a 'customPostOrder' or 'reorder' permissions set to false, and set to 'first to last'
          if (this.project.customPostOrder || !this.project.reorder) {
            this._set(state.posts.sortNewest, false)
            this._set(state.posts.customPostOrder, true)
          } else {
            this._set(state.posts.sortNewest, this.sortNewestFirst)
          }
          this._set(state.posts.repliesExpanded, !this.collapseComments)
          if (project.qrLink === undefined && (this.$isIsef || this.$isYSC)) {
            this.$store.dispatch('getProjectStatus', { projectID: this.project.id }).then((response) => {
              if (response.approved) {
                this.$store.dispatch('generateQRCode', { projectID: this.project.id }).then((res) => {
                  this._set(state.project.qrLink, res.qrCodeURL)
                })
              }
            })
          } else {
            this._set(state.project.qrLink, project.qrLink)
          }
          this.setPermissions()
          this.fetchPosts()

          socket.view('project', project.id)
          this.autoFollow()
          this.trackRecommendation()
          if (this.$isEngCom) {
            this.trackStoryAnalytics(project)
          }

          if (this.fromTemplate) {
            this._set(state.project.header.edit, true)
          }

          if ((this.$isYSC && this.projectDisplayType === this.DisplayTypeRegistrationForm && !this.$store.getters.onboardingPreviouslyCompleted('judgeregistration')) || this.$route.query.onboard === '1') {
            this.$store.dispatch('showJudgingOnboardingTutorialModal')
          }
          if (project.displayType === this.DisplayTypeRegistrationForm) {
            this._set(state.project.header.edit, false)
          }

          if (project.displayType === this.DisplayTypeJudgeTeam) {
            this.$store.dispatch('getJudgingGroupsForProject', this.project.id).then((groups) => {
              this._set(state.project.judgeGroups, groups)
            }, (err) => {
              console.error(err)
            })
          }

          this.$store.dispatch('fetchPollQuestions', { projectId: project.id })
          this.$store.dispatch('fetchPollUserOptions', { projectId: project.id })
        }, () => {
          this._set(state.project.loading, false)
          this._set(state.project.notFound, true)
          this._set(state.activeTab, 'not-found')
        })
      })
    },
    fetchPosts: function () {
      const hasMore = this._get(state.posts.hasMore, true)
      if (!hasMore || this._get(state.posts.loading)) {
        return
      }

      this._set(state.posts.loading, true)
      const page = this._get(state.posts.page, 1)
      const sort = this._get(state.posts.sortNewest, this.sortNewestFirst) ? 'newest' : 'oldest'
      const limit = this.limit

      const params = { id: this.project.id, limit, sort, page }

      if ((this.fromTemplate || this.fromStartButtonTemplate) && page === 1) {
        this._set(state.posts.edit, true)
      }
      if (params.id) {
        this.$store.dispatch('getProjectPosts', params).then((response) => {
          this._set(state.posts.loading, false)
          if (page === 1) {
            this._set(state.posts.total, response.total)
          }
          this._set(state.posts.page, page + 1)
          this._set(state.posts.hasMore, response.hasMore)
          this._set(state.paywall, response.paywall)
          this._set(state.posts.repliesExpanded, true)
        }, () => {
          this._set(state.posts.loading, false)
        })
      } else {
        this._set(state.posts.loading, false)
      }
    },
    fetchToPost: function (id) {
      this._set(state.posts.loading, true)

      const page = this._get(state.posts.page, 1)
      const sort = this._get(state.posts.sortNewest, false) ? 'newest' : 'oldest'
      const limit = this.limit

      const params = { id: this.project.id, postID: id, sort, limit }

      this.$store.dispatch('loadToPost', params).then((response) => {
        this._set(state.posts.loading, false)
        this._set(state.posts.page, page + 1)
        this._set(state.posts.total, response.total)
        this._set(state.posts.hasMore, response.hasMore)
      }, () => {
        this._set(state.posts.loading, false)
      })
    },
    sortPosts: function () {
      const sort = !this._get(state.posts.sortNewest, this.sortNewestFirst)
      this._set(state.posts.sortNewest, sort)
      this.updateSortNewestFirstSetting(sort)

      if (this._get(state.posts.hasMore, true)) {
        this._set(state.posts.page, 1)
        return this.fetchPosts()
      }
    },
    updateSortNewestFirstSetting: throttle(function (sort) {
      if (this.isLoggedIn) {
        const settings = { sortNewestFirst: sort }
        this.$store.dispatch('updateUserSettings', { username: this.currentUser.userName, settings }).then(() => {
          this.sortNewestFirst = sort
        })
      }
    }, 1000, { leading: true, trailing: false }),
    fetchMembership: function () {
      return this.$store.dispatch('getMembership', {
        project: this.project,
        user: this.currentUser
      }).then((membership) => {
        this._set(state.project.role, membership ? membership.role : 'none')
      }, () => {
        this._set(state.project.role, null)
      })
    },
    setPermissions: function () {
      this._set(state.posts.permissions.create, false)
      this._set(state.posts.permissions.edit, false)
      this._set(state.comments.permissions.create, !this.project.disableComments)

      this.getProject = this.$store.getters.project(this._get(state.project.id))

      if (!this.isLoggedIn) {
        return
      }

      if (state.project.type === 'snapshot') return

      if (this.isAdmin) {
        this._set(state.posts.permissions.create, true)
        this._set(state.posts.permissions.edit, true)
        this._set(state.comments.permissions.create, true)
        this._set(state.project.permissions.edit, true)
        this._set(state.project.permissions.chat, true)
        this._set(state.project.permissions.liveChat, true)
        return
      }

      const isDuringDisableEditPeriod = this.duringDisableEditPeriod(this.project.tags, this.project.created)
      if (isDuringDisableEditPeriod) {
        return
      }

      this.fetchMembership().then(() => {
        const isOwner = state.project.user === this.currentUser.uuid || this.projectRole === 'owner'
        const isAnonCoordinator = state.project.user === this.currentUser.uuid || this.projectRole === 'anonCoordinator'
        if (this.projectRole === 'co-owner' || this.projectRole === 'anonAdmin' || this.projectRole === 'admin' || isOwner || isAnonCoordinator) {
          this._set(state.posts.permissions.create, true)
          this._set(state.posts.permissions.edit, true)
          this._set(state.comments.permissions.create, true)
          this._set(state.project.permissions.edit, true)
        } else if (this.projectRole === 'contributor' || this.projectRole === 'anonContributor') {
          this._set(state.posts.permissions.create, true)
          this._set(state.comments.permissions.create, true)
        } else if (this.project.isPrivate) {
          this._set(state.posts.permissions.create, false)
          this._set(state.comments.permissions.create, false)
        } else if (this.projectRole === 'anonCoordinator') {
          this._set(state.project.permissions.edit, true)
        }
        if (isOwner || this.projectRole === 'co-owner' || this.projectRole === 'contributor' || this.projectRole === 'viewer') {
          this._set(state.project.permissions.chat, true)
        }
        if (isOwner || this.projectRole === 'co-owner' || this.projectRole === 'anonAdmin' || this.projectRole === 'admin') {
          this._set(state.project.permissions.liveChat, true)
        }
      }).finally(() => {
        if (this.project.submitted) {
          this._set(state.posts.permissions.edit, false)
        }
      })

      if (this.project.submitted) {
        this._set(state.posts.permissions.edit, false)
      }
    },
    autoFollow: function () {
      if (this.$route.query.follow) {
        if (this.isLoggedIn) {
          var autoFollow = this._get(state.project.id)
          const data = { autoFollow: parseInt(autoFollow) }
          this.$store.dispatch('autoFollowProject', { username: this.currentUser.userName, data })
        } else {
          this.$store.dispatch('showLoginModal', {
            source: 'FOLLOW:{PATH} ' + this.$route.query.follow,
            callback: this.autoFollow,
            signupMessage: 'Sign up to follow this project'
          })
        }
      }
    },
    trackRecommendation: function () {
      const encoded = this.$route && this.$route.query.rec ? this.$route.query.rec : ''
      if (!encoded) {
        return
      }

      try {
        const rec = atob(encoded).split('|')
        if (rec.length === 2) {
          socket.recommendation(rec[1], parseInt(rec[0]), this.project.id)
        }

        const query = Object.assign({}, this.$route.query)
        delete query.rec
        this.$router.replace({ query })
      } catch (error) {
      }
    },
    unsavedModalYes: function () {
      if (this.fromTemplate) {
        this.$refs.projectHeader.deleteProject(false)
      }
      this.nextPage()
    },
    unsavedModalNo: function () {
      // if they hit back, window location may no longer match
      if (this.$route.path !== window.location.pathname) {
        // puts the browser history state back to this route
        this.$router.go(1)
      }
    },
    toggleReplies: function () {
      const expanded = !this._get(state.posts.repliesExpanded, !this.collapseComments)
      this._set(state.posts.repliesExpanded, expanded)
      this.updateCollapseCommentsSetting(expanded)
    },
    updateCollapseCommentsSetting: throttle(function (expanded) {
      if (this.isLoggedIn) {
        const settings = { collapseComments: !expanded }
        this.$store.dispatch('updateUserSettings', { username: this.currentUser.userName, settings }).then(() => {
          this.collapseComments = !expanded
        })
      }
    }, 1000, { leading: true, trailing: false }),
    initLinkClicks: function () {
      const projectOrArticle = (this.$route.name === 'viewStory' ? 'storyView' : 'projectView')
      this.$refs[projectOrArticle].addEventListener('click', this.checkIsLink)
    },
    checkIsLink: function (e) {
      const href = e.target?.closest('a')?.href || ''
      if (href) {
        this.logLinkClick(href)
      }
    },
    logLinkClick: function (url) {
      const id = this.project.id
      projectClicks.logLinkClick(id, url)
    }
  }
}
